import { useState, useEffect, useMemo } from 'react';
import { adminApi, ApiError } from '../lib/api';
import { DashboardLayout } from '../components/DashboardLayout';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import './UserManagementPage.css';

interface UserRecord {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  status: 'ACTIVE' | 'INACTIVE' | 'SUSPENDED';
  isSuperAdmin: boolean;
  moduleAccess?: {
    seo?: boolean;
    ai?: boolean;
    reports?: boolean;
    [key: string]: boolean | undefined;
  };
  createdAt: string;
  updatedAt?: string;
}

export default function UserManagementPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState<UserRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Filtering & Search
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [roleFilter, setRoleFilter] = useState('ALL');
  const [copiedEmailId, setCopiedEmailId] = useState<number | null>(null);

  // User Onboarding Modal State
  const [isOnboardOpen, setIsOnboardOpen] = useState(false);
  const [onboardForm, setOnboardForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    isSuperAdmin: false,
    status: 'ACTIVE' as 'ACTIVE' | 'INACTIVE',
    moduleAccess: { seo: true, ai: true, reports: true },
  });
  const [onboardLoading, setOnboardLoading] = useState(false);
  const [onboardError, setOnboardError] = useState('');

  // Edit User Details Modal State
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({
    id: 0,
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    isSuperAdmin: false,
    status: 'ACTIVE' as 'ACTIVE' | 'INACTIVE' | 'SUSPENDED',
    moduleAccess: { seo: true, ai: true, reports: true },
  });
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState('');

  useEffect(() => {
    if (user && !user.isSuperAdmin) {
      navigate('/dashboard');
      return;
    }
    
    adminApi.getUsers()
      .then(res => {
        const userList = Array.isArray(res) 
          ? res 
          : (res as any)?.data && Array.isArray((res as any).data) 
            ? (res as any).data 
            : [];
        setUsers(userList);
        setLoading(false);
      })
      .catch((err: ApiError) => {
        setError(err.message || 'Failed to load users');
        setLoading(false);
      });
  }, [user, navigate]);

  const handleStatusChange = async (userId: number, newStatus: string) => {
    const targetUser = users.find(u => u.id === userId);
    if (targetUser?.isSuperAdmin) {
      alert('Super Admin status is permanently active and cannot be modified.');
      return;
    }
    if (user?.id === userId) {
      alert('You cannot modify your own account status.');
      return;
    }
    try {
      await adminApi.updateUser(userId, { status: newStatus });
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, status: newStatus as any } : u));
    } catch (err: any) {
      alert(err.message || 'Failed to update user status');
    }
  };

  const handleModuleAccessChange = async (userId: number, module: string, hasAccess: boolean) => {
    const targetUser = users.find(u => u.id === userId);
    if (!targetUser) return;
    
    if (targetUser.isSuperAdmin) {
      alert('Super Admin accounts have permanent full access to all modules.');
      return;
    }
    if (user?.id === userId) {
      alert('You cannot revoke your own module access.');
      return;
    }
    
    const currentAccess = targetUser.moduleAccess || {};
    const updatedAccess = { ...currentAccess, [module]: hasAccess };
    
    try {
      await adminApi.updateUser(userId, { moduleAccess: updatedAccess });
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, moduleAccess: updatedAccess } : u));
    } catch (err: any) {
      alert(err.message || 'Failed to update module access');
    }
  };

  const handleRoleChange = async (userId: number, newIsSuperAdmin: boolean) => {
    if (user?.id === userId && !newIsSuperAdmin) {
      alert('You cannot revoke your own Super Admin access.');
      return;
    }
    const targetUser = users.find(u => u.id === userId);
    if (!targetUser) return;

    const confirmMsg = newIsSuperAdmin
      ? `Promote ${targetUser.firstName} ${targetUser.lastName} to Super Admin? They will receive full administrative access.`
      : `Demote ${targetUser.firstName} ${targetUser.lastName} to standard Member?`;

    if (!window.confirm(confirmMsg)) return;

    try {
      await adminApi.updateUser(userId, { 
        isSuperAdmin: newIsSuperAdmin,
        // When promoting to super admin, grant all module permissions
        ...(newIsSuperAdmin ? { moduleAccess: { seo: true, ai: true, reports: true } } : {})
      });
      setUsers(prev => prev.map(u => {
        if (u.id === userId) {
          return {
            ...u,
            isSuperAdmin: newIsSuperAdmin,
            moduleAccess: newIsSuperAdmin 
              ? { seo: true, ai: true, reports: true } 
              : u.moduleAccess
          };
        }
        return u;
      }));
    } catch (err: any) {
      alert(err.message || 'Failed to update user role');
    }
  };

  const handleOnboardSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!onboardForm.firstName.trim() || !onboardForm.lastName.trim() || !onboardForm.email.trim()) {
      setOnboardError('Please fill in First Name, Last Name, and Email.');
      return;
    }
    setOnboardLoading(true);
    setOnboardError('');
    try {
      const res = await adminApi.createUser({
        firstName: onboardForm.firstName.trim(),
        lastName: onboardForm.lastName.trim(),
        email: onboardForm.email.trim(),
        password: onboardForm.password.trim() || undefined,
        isSuperAdmin: onboardForm.isSuperAdmin,
        status: onboardForm.status,
        moduleAccess: onboardForm.isSuperAdmin 
          ? { seo: true, ai: true, reports: true } 
          : onboardForm.moduleAccess,
      });
      const created = res.data || res;
      setUsers(prev => [created, ...prev]);
      setIsOnboardOpen(false);
      setOnboardForm({
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        isSuperAdmin: false,
        status: 'ACTIVE',
        moduleAccess: { seo: true, ai: true, reports: true },
      });
    } catch (err: any) {
      setOnboardError(err.message || 'Failed to onboard user');
    } finally {
      setOnboardLoading(false);
    }
  };

  const copyEmail = (userId: number, email: string) => {
    navigator.clipboard?.writeText(email);
    setCopiedEmailId(userId);
    setTimeout(() => setCopiedEmailId(null), 2000);
  };

  const handleDeleteUser = async (userId: number, userName: string) => {
    if (user?.id === userId) {
      alert('You cannot delete your own account.');
      return;
    }
    const confirmed = window.confirm(
      `Are you sure you want to permanently delete user "${userName}"?\n\nThis will remove their account, active sessions, and permissions. This action cannot be undone.`
    );
    if (!confirmed) return;

    try {
      await adminApi.deleteUser(userId);
      setUsers(prev => prev.filter(u => u.id !== userId));
    } catch (err: any) {
      alert(err.message || 'Failed to delete user');
    }
  };

  const handleOpenEdit = (userToEdit: UserRecord) => {
    setEditError('');
    setEditForm({
      id: userToEdit.id,
      firstName: userToEdit.firstName || '',
      lastName: userToEdit.lastName || '',
      email: userToEdit.email || '',
      password: '',
      isSuperAdmin: Boolean(userToEdit.isSuperAdmin),
      status: userToEdit.status || 'ACTIVE',
      moduleAccess: {
        seo: userToEdit.moduleAccess?.seo !== false,
        ai: userToEdit.moduleAccess?.ai !== false,
        reports: userToEdit.moduleAccess?.reports !== false,
      },
    });
    setIsEditOpen(true);
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editForm.firstName.trim() || !editForm.lastName.trim() || !editForm.email.trim()) {
      setEditError('First Name, Last Name, and Email are required.');
      return;
    }
    setEditLoading(true);
    setEditError('');
    try {
      const res = await adminApi.updateUser(editForm.id, {
        firstName: editForm.firstName.trim(),
        lastName: editForm.lastName.trim(),
        email: editForm.email.trim(),
        password: editForm.password.trim() || undefined,
        isSuperAdmin: editForm.isSuperAdmin,
        status: editForm.status,
        moduleAccess: editForm.isSuperAdmin 
          ? { seo: true, ai: true, reports: true } 
          : editForm.moduleAccess,
      });
      const updated = res.data || res;
      setUsers(prev => prev.map(u => u.id === editForm.id ? { ...u, ...updated } : u));
      setIsEditOpen(false);
    } catch (err: any) {
      setEditError(err.message || 'Failed to update user details');
    } finally {
      setEditLoading(false);
    }
  };

  // Metrics
  const metrics = useMemo(() => {
    const total = users.length;
    const active = users.filter(u => u.status === 'ACTIVE').length;
    const superAdmins = users.filter(u => u.isSuperAdmin).length;
    const fullAccess = users.filter(u => {
      const access = u.moduleAccess;
      return access?.seo !== false && access?.ai !== false && access?.reports !== false;
    }).length;
    return { total, active, superAdmins, fullAccess };
  }, [users]);

  // Filtered users
  const filteredUsers = useMemo(() => {
    return users.filter(u => {
      // Search query
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase().trim();
        const fullName = `${u.firstName || ''} ${u.lastName || ''}`.toLowerCase();
        const email = (u.email || '').toLowerCase();
        if (!fullName.includes(query) && !email.includes(query)) {
          return false;
        }
      }

      // Status filter
      if (statusFilter !== 'ALL' && u.status !== statusFilter) {
        return false;
      }

      // Role filter
      if (roleFilter === 'SUPER_ADMIN' && !u.isSuperAdmin) {
        return false;
      }
      if (roleFilter === 'USER' && u.isSuperAdmin) {
        return false;
      }

      return true;
    });
  }, [users, searchQuery, statusFilter, roleFilter]);

  const clearFilters = () => {
    setSearchQuery('');
    setStatusFilter('ALL');
    setRoleFilter('ALL');
  };

  if (loading) {
    return (
      <DashboardLayout title="User Management">
        <div className="ump-container">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '320px', gap: '12px', color: '#64748b' }}>
            <svg style={{ animation: 'spin 1s linear infinite', width: '28px', height: '28px' }} viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="10" stroke="#e2e8f0" strokeWidth="4" />
              <path fill="#2563eb" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
            <span style={{ fontSize: '15px', fontWeight: 500 }}>Loading user management...</span>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="User Management">
      <div className="ump-container">
        
        {/* Header Section */}
        <div className="ump-header">
          <div>
            <h1 className="ump-header-title">User Management</h1>
            <p className="ump-header-subtitle">
              Manage accounts, roles, access permissions, and account status across your organization.
            </p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            <div className="ump-header-badge">
              <span className="ump-pulse-dot" />
              <span>{users.length} Registered Accounts</span>
            </div>
            <button 
              type="button" 
              className="ump-btn-onboard"
              onClick={() => {
                setOnboardError('');
                setIsOnboardOpen(true);
              }}
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
              Onboard User
            </button>
          </div>
        </div>

        {/* Error notification */}
        {error && (
          <div style={{
            background: '#fef2f2',
            border: '1px solid #fecaca',
            color: '#991b1b',
            padding: '12px 16px',
            borderRadius: '10px',
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            fontSize: '14px'
          }}>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <strong>{error}</strong>
          </div>
        )}

        {/* Top KPI Metric Cards */}
        <div className="ump-kpi-grid">
          <div className="ump-kpi-card">
            <div className="ump-kpi-icon-wrap blue">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
              </svg>
            </div>
            <div className="ump-kpi-info">
              <span className="ump-kpi-label">Total Users</span>
              <span className="ump-kpi-value">{metrics.total}</span>
            </div>
          </div>

          <div className="ump-kpi-card">
            <div className="ump-kpi-icon-wrap green">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                <polyline points="22 4 12 14.01 9 11.01" />
              </svg>
            </div>
            <div className="ump-kpi-info">
              <span className="ump-kpi-label">Active Users</span>
              <span className="ump-kpi-value">{metrics.active}</span>
            </div>
          </div>

          <div className="ump-kpi-card">
            <div className="ump-kpi-icon-wrap purple">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              </svg>
            </div>
            <div className="ump-kpi-info">
              <span className="ump-kpi-label">Super Admins</span>
              <span className="ump-kpi-value">{metrics.superAdmins}</span>
            </div>
          </div>

          <div className="ump-kpi-card">
            <div className="ump-kpi-icon-wrap amber">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
              </svg>
            </div>
            <div className="ump-kpi-info">
              <span className="ump-kpi-label">Full Module Access</span>
              <span className="ump-kpi-value">{metrics.fullAccess}</span>
            </div>
          </div>
        </div>

        {/* Toolbar: Search and Filter Controls */}
        <div className="ump-toolbar">
          <div className="ump-search-wrapper">
            <div className="ump-search-icon">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
            </div>
            <input 
              type="text" 
              className="ump-search-input" 
              placeholder="Search by user name or email..." 
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button 
                type="button" 
                className="ump-search-clear" 
                onClick={() => setSearchQuery('')}
                title="Clear search"
              >
                ✕
              </button>
            )}
          </div>

          <div className="ump-filters">
            <select 
              className="ump-filter-select"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="ALL">All Statuses</option>
              <option value="ACTIVE">Active Only</option>
              <option value="INACTIVE">Inactive Only</option>
              <option value="SUSPENDED">Suspended Only</option>
            </select>

            <select 
              className="ump-filter-select"
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
            >
              <option value="ALL">All Roles</option>
              <option value="SUPER_ADMIN">Super Admins</option>
              <option value="USER">Members</option>
            </select>
          </div>
        </div>

        {/* Modern Data Table */}
        <div className="ump-table-card">
          <div className="ump-table-scroll">
            <table className="ump-table">
              <thead>
                <tr>
                  <th style={{ width: '28%' }}>User Details</th>
                  <th style={{ width: '13%' }}>Role</th>
                  <th style={{ width: '15%' }}>Status</th>
                  <th style={{ width: '24%' }}>Module Permissions</th>
                  <th style={{ width: '12%' }}>Joined</th>
                  <th style={{ width: '8%', textAlign: 'center' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.length === 0 ? (
                  <tr>
                    <td colSpan={6} style={{ padding: 0 }}>
                      <div className="ump-empty-state">
                        <div className="ump-empty-icon">
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="11" cy="11" r="8" />
                            <line x1="21" y1="21" x2="16.65" y2="16.65" />
                          </svg>
                        </div>
                        <div className="ump-empty-title">No users found</div>
                        <p className="ump-empty-desc">
                          No users match the current search query or active filter criteria.
                        </p>
                        <button type="button" className="ump-btn-reset" onClick={clearFilters}>
                          Reset All Filters
                        </button>
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredUsers.map((u) => {
                    const firstInitial = (u.firstName?.[0] || '').toUpperCase();
                    const lastInitial = (u.lastName?.[0] || '').toUpperCase();
                    const initials = `${firstInitial}${lastInitial}` || 'U';

                    const isCopied = copiedEmailId === u.id;
                    const isSelf = user?.id === u.id;
                    const isSuperAdmin = Boolean(u.isSuperAdmin);
                    const isProtected = isSuperAdmin || isSelf;

                    return (
                      <tr key={u.id}>
                        {/* User Details */}
                        <td>
                          <div className="ump-user-cell">
                            <div className={`ump-avatar ${isSuperAdmin ? 'superadmin' : 'standard'}`}>
                              {initials}
                            </div>
                            <div className="ump-user-info">
                              <div className="ump-user-name-row">
                                <span className="ump-user-name" title={`${u.firstName} ${u.lastName}`}>
                                  {u.firstName} {u.lastName}
                                </span>
                                {isSuperAdmin && (
                                  <span title="Super Administrator" style={{ color: '#7c3aed', fontSize: '13px', lineHeight: 1 }}>
                                    ★
                                  </span>
                                )}
                                {isSelf && (
                                  <span className="ump-you-tag" title="Currently logged in user">
                                    You
                                  </span>
                                )}
                              </div>
                              <span 
                                className="ump-user-email" 
                                title={u.email}
                                onClick={() => copyEmail(u.id, u.email)}
                                style={{ cursor: 'pointer' }}
                              >
                                {u.email}
                              </span>
                            </div>
                          </div>
                        </td>

                        {/* Role */}
                        <td>
                          <div className="ump-role-cell">
                            {isSuperAdmin ? (
                              <span className="ump-role-badge superadmin">
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                                  <path d="M12 2L1 21h22L12 2zm0 3.99L19.53 19H4.47L12 5.99zM11 16h2v2h-2zm0-6h2v4h-2z"/>
                                </svg>
                                Super Admin
                              </span>
                            ) : (
                              <span className="ump-role-badge user">
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                                  <circle cx="12" cy="7" r="4" />
                                </svg>
                                Member
                              </span>
                            )}

                            {/* Set as Admin / Demote Action */}
                            {!isSelf && (
                              !isSuperAdmin ? (
                                <button
                                  type="button"
                                  className="ump-role-action-btn"
                                  onClick={() => handleRoleChange(u.id, true)}
                                  title="Promote this user to Super Admin"
                                >
                                  + Set as Admin
                                </button>
                              ) : (
                                <button
                                  type="button"
                                  className="ump-role-action-btn demote"
                                  onClick={() => handleRoleChange(u.id, false)}
                                  title="Demote this user to Member"
                                >
                                  Demote to Member
                                </button>
                              )
                            )}
                          </div>
                        </td>

                        {/* Status */}
                        <td>
                          <div className="ump-status-select-wrap">
                            <span className={`ump-status-dot ${u.status}`} />
                            <select
                              value={u.status}
                              disabled={isProtected}
                              onChange={(e) => handleStatusChange(u.id, e.target.value)}
                              className={`ump-status-select ${u.status}`}
                              aria-label={`Status for ${u.firstName} ${u.lastName}`}
                              title={
                                isSelf
                                  ? "You cannot modify your own status"
                                  : isSuperAdmin
                                    ? "Super Admin status is permanently active"
                                    : "Change user status"
                              }
                            >
                              <option value="ACTIVE">Active</option>
                              <option value="INACTIVE">Inactive</option>
                              <option value="SUSPENDED">Suspended</option>
                            </select>
                            {!isProtected ? (
                              <span className="ump-status-chevron">
                                <svg width="12" height="12" viewBox="0 0 20 20" fill="currentColor">
                                  <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd"/>
                                </svg>
                              </span>
                            ) : (
                              <span className="ump-status-chevron" style={{ opacity: 0.4 }}>
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                                  <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z"/>
                                </svg>
                              </span>
                            )}
                          </div>
                        </td>

                        {/* Module Permissions (Interactive Pill Chips) */}
                        <td>
                          <div className="ump-perms-row">
                            {[
                              { key: 'seo', label: 'SEO' },
                              { key: 'ai', label: 'AI' },
                              { key: 'reports', label: 'Reports' },
                            ].map((mod) => {
                              const isChecked = isSuperAdmin ? true : u.moduleAccess?.[mod.key] !== false;
                              return (
                                <button
                                  key={mod.key}
                                  type="button"
                                  disabled={isProtected}
                                  className={`ump-perm-chip ${isChecked ? 'enabled' : 'disabled'} ${isProtected ? 'locked' : ''}`}
                                  onClick={() => !isProtected && handleModuleAccessChange(u.id, mod.key, !isChecked)}
                                  title={
                                    isSelf
                                      ? "You cannot revoke your own module access"
                                      : isSuperAdmin
                                        ? "Super Admin has permanent full access to all modules"
                                        : `Click to ${isChecked ? 'disable' : 'enable'} ${mod.label} module for this user`
                                  }
                                >
                                  <span className="ump-perm-icon">
                                    {isChecked ? (
                                      <svg width="12" height="12" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                                      </svg>
                                    ) : (
                                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                                        <line x1="18" y1="6" x2="6" y2="18" />
                                        <line x1="6" y1="6" x2="18" y2="18" />
                                      </svg>
                                    )}
                                  </span>
                                  {mod.label}
                                </button>
                              );
                            })}
                            {isProtected && (
                              <span 
                                title={isSelf ? "Self-revocation disabled" : "Super Admin access locked"}
                                style={{ display: 'inline-flex', alignItems: 'center', color: '#94a3b8', marginLeft: '2px' }}
                              >
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                                  <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z"/>
                                </svg>
                              </span>
                            )}
                          </div>
                        </td>

                        {/* Joined Date */}
                        <td>
                          <div className="ump-date-cell">
                            <span className="ump-date-main">
                              {new Date(u.createdAt).toLocaleDateString(undefined, { 
                                month: 'short', 
                                day: 'numeric', 
                                year: 'numeric' 
                              })}
                            </span>
                            <span className="ump-date-time">
                              {new Date(u.createdAt).toLocaleTimeString(undefined, { 
                                hour: 'numeric', 
                                minute: '2-digit' 
                              })}
                            </span>
                          </div>
                        </td>

                        {/* Actions */}
                        <td style={{ textAlign: 'center' }}>
                          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', justifyContent: 'center' }}>
                            <button
                              type="button"
                              onClick={() => copyEmail(u.id, u.email)}
                              title={isCopied ? 'Email copied!' : 'Copy email address'}
                              className="ump-action-btn"
                            >
                              {isCopied ? (
                                <svg width="14" height="14" viewBox="0 0 20 20" fill="currentColor">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                                </svg>
                              ) : (
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                                  <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                                </svg>
                              )}
                            </button>

                            <button
                              type="button"
                              onClick={() => handleOpenEdit(u)}
                              title={`Edit user details for ${u.firstName} ${u.lastName}`}
                              className="ump-action-btn edit"
                            >
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                              </svg>
                            </button>

                            {!isSelf && (
                              <button
                                type="button"
                                onClick={() => handleDeleteUser(u.id, `${u.firstName} ${u.lastName}`)}
                                title={`Delete user ${u.firstName} ${u.lastName}`}
                                className="ump-action-btn delete"
                              >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <polyline points="3 6 5 6 21 6" />
                                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                                  <line x1="10" y1="11" x2="10" y2="17" />
                                  <line x1="14" y1="11" x2="14" y2="17" />
                                </svg>
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Table Footer */}
          <div className="ump-table-footer">
            <span>
              Showing <strong>{filteredUsers.length}</strong> of <strong>{users.length}</strong> accounts
            </span>
            {searchQuery || statusFilter !== 'ALL' || roleFilter !== 'ALL' ? (
              <span style={{ color: '#2563eb', cursor: 'pointer', fontWeight: 600 }} onClick={clearFilters}>
                Clear Active Filters
              </span>
            ) : (
              <span style={{ color: '#94a3b8' }}>
                All permissions live-synced
              </span>
            )}
          </div>
        </div>

        {/* User Onboarding Modal */}
        {isOnboardOpen && (
          <div className="ump-modal-backdrop" onClick={() => !onboardLoading && setIsOnboardOpen(false)}>
            <div className="ump-modal-card" onClick={(e) => e.stopPropagation()}>
              <div className="ump-modal-header">
                <div>
                  <h2 className="ump-modal-title">Onboard New User</h2>
                  <p className="ump-modal-subtitle">
                    Add a new team member, configure their system role, and grant module permissions.
                  </p>
                </div>
                <button
                  type="button"
                  className="ump-modal-close-btn"
                  onClick={() => !onboardLoading && setIsOnboardOpen(false)}
                  title="Close"
                >
                  ✕
                </button>
              </div>

              <form onSubmit={handleOnboardSubmit}>
                <div className="ump-modal-body">
                  {onboardError && (
                    <div style={{
                      background: '#fef2f2',
                      border: '1px solid #fecaca',
                      color: '#991b1b',
                      padding: '10px 14px',
                      borderRadius: '8px',
                      fontSize: '13px'
                    }}>
                      {onboardError}
                    </div>
                  )}

                  <div className="ump-form-grid-2">
                    <div className="ump-form-group">
                      <label className="ump-form-label required">First Name</label>
                      <input
                        type="text"
                        className="ump-modal-input"
                        placeholder="e.g. Alex"
                        required
                        value={onboardForm.firstName}
                        onChange={(e) => setOnboardForm({ ...onboardForm, firstName: e.target.value })}
                      />
                    </div>
                    <div className="ump-form-group">
                      <label className="ump-form-label required">Last Name</label>
                      <input
                        type="text"
                        className="ump-modal-input"
                        placeholder="e.g. Mercer"
                        required
                        value={onboardForm.lastName}
                        onChange={(e) => setOnboardForm({ ...onboardForm, lastName: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="ump-form-group">
                    <label className="ump-form-label required">Email Address</label>
                    <input
                      type="email"
                      className="ump-modal-input"
                      placeholder="alex.mercer@company.com"
                      required
                      value={onboardForm.email}
                      onChange={(e) => setOnboardForm({ ...onboardForm, email: e.target.value })}
                    />
                  </div>

                  <div className="ump-form-group">
                    <label className="ump-form-label">Temporary Password</label>
                    <input
                      type="text"
                      className="ump-modal-input"
                      placeholder="Leave blank to use default (Welcome@2026!)"
                      value={onboardForm.password}
                      onChange={(e) => setOnboardForm({ ...onboardForm, password: e.target.value })}
                    />
                    <span style={{ fontSize: '11.5px', color: '#94a3b8' }}>
                      Defaults to <code>Welcome@2026!</code> if not specified.
                    </span>
                  </div>

                  <div className="ump-form-group">
                    <label className="ump-form-label">System Role</label>
                    <div className="ump-role-radio-group">
                      <div
                        className={`ump-role-radio-card ${!onboardForm.isSuperAdmin ? 'selected' : ''}`}
                        onClick={() => setOnboardForm({ ...onboardForm, isSuperAdmin: false })}
                      >
                        <span className="ump-role-card-title">
                          <span>👤</span> Standard Member
                        </span>
                        <span className="ump-role-card-desc">
                          Regular access based on assigned module permissions.
                        </span>
                      </div>
                      <div
                        className={`ump-role-radio-card ${onboardForm.isSuperAdmin ? 'selected admin' : ''}`}
                        onClick={() => setOnboardForm({ ...onboardForm, isSuperAdmin: true })}
                      >
                        <span className="ump-role-card-title" style={{ color: onboardForm.isSuperAdmin ? '#6d28d9' : 'inherit' }}>
                          <span>★</span> Super Admin
                        </span>
                        <span className="ump-role-card-desc">
                          Full system control, user management, and unrestricted access.
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="ump-form-group">
                    <label className="ump-form-label">Module Permissions</label>
                    <div className="ump-perms-row" style={{ marginTop: '2px' }}>
                      {[
                        { key: 'seo', label: 'SEO Intelligence' },
                        { key: 'ai', label: 'AI Assistant' },
                        { key: 'reports', label: 'Reports' },
                      ].map((mod) => {
                        const isChecked = onboardForm.isSuperAdmin || (onboardForm.moduleAccess as any)[mod.key] !== false;
                        return (
                          <button
                            key={mod.key}
                            type="button"
                            disabled={onboardForm.isSuperAdmin}
                            className={`ump-perm-chip ${isChecked ? 'enabled' : 'disabled'} ${onboardForm.isSuperAdmin ? 'locked' : ''}`}
                            onClick={() => {
                              if (!onboardForm.isSuperAdmin) {
                                setOnboardForm({
                                  ...onboardForm,
                                  moduleAccess: {
                                    ...onboardForm.moduleAccess,
                                    [mod.key]: !isChecked
                                  }
                                });
                              }
                            }}
                          >
                            <span className="ump-perm-icon">
                              {isChecked ? (
                                <svg width="12" height="12" viewBox="0 0 20 20" fill="currentColor">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                                </svg>
                              ) : (
                                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                                  <line x1="18" y1="6" x2="6" y2="18" />
                                  <line x1="6" y1="6" x2="18" y2="18" />
                                </svg>
                              )}
                            </span>
                            {mod.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>

                <div className="ump-modal-footer">
                  <button
                    type="button"
                    className="ump-btn-cancel"
                    disabled={onboardLoading}
                    onClick={() => setIsOnboardOpen(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="ump-btn-submit"
                    disabled={onboardLoading}
                  >
                    {onboardLoading ? 'Onboarding...' : 'Onboard User'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Edit User Details Modal */}
        {isEditOpen && (
          <div className="ump-modal-backdrop" onClick={() => !editLoading && setIsEditOpen(false)}>
            <div className="ump-modal-card" onClick={(e) => e.stopPropagation()}>
              <div className="ump-modal-header">
                <div>
                  <h2 className="ump-modal-title">Edit User Details</h2>
                  <p className="ump-modal-subtitle">
                    Update user personal information, account role, and access privileges.
                  </p>
                </div>
                <button
                  type="button"
                  className="ump-modal-close-btn"
                  onClick={() => !editLoading && setIsEditOpen(false)}
                  title="Close"
                >
                  ✕
                </button>
              </div>

              <form onSubmit={handleEditSubmit}>
                <div className="ump-modal-body">
                  {editError && (
                    <div style={{
                      background: '#fef2f2',
                      border: '1px solid #fecaca',
                      color: '#991b1b',
                      padding: '10px 14px',
                      borderRadius: '8px',
                      fontSize: '13px'
                    }}>
                      {editError}
                    </div>
                  )}

                  <div className="ump-form-grid-2">
                    <div className="ump-form-group">
                      <label className="ump-form-label required">First Name</label>
                      <input
                        type="text"
                        className="ump-modal-input"
                        required
                        value={editForm.firstName}
                        onChange={(e) => setEditForm({ ...editForm, firstName: e.target.value })}
                      />
                    </div>
                    <div className="ump-form-group">
                      <label className="ump-form-label required">Last Name</label>
                      <input
                        type="text"
                        className="ump-modal-input"
                        required
                        value={editForm.lastName}
                        onChange={(e) => setEditForm({ ...editForm, lastName: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="ump-form-group">
                    <label className="ump-form-label required">Email Address</label>
                    <input
                      type="email"
                      className="ump-modal-input"
                      required
                      value={editForm.email}
                      onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                    />
                  </div>

                  <div className="ump-form-group">
                    <label className="ump-form-label">Change Password</label>
                    <input
                      type="text"
                      className="ump-modal-input"
                      placeholder="Leave blank to keep existing password unchanged"
                      value={editForm.password}
                      onChange={(e) => setEditForm({ ...editForm, password: e.target.value })}
                    />
                    <span style={{ fontSize: '11.5px', color: '#94a3b8' }}>
                      Only enter a new password if you wish to reset it for this user.
                    </span>
                  </div>

                  <div className="ump-form-group">
                    <label className="ump-form-label">Account Status</label>
                    <select
                      className="ump-modal-input"
                      style={{ cursor: 'pointer' }}
                      value={editForm.status}
                      disabled={editForm.isSuperAdmin || editForm.id === user?.id}
                      onChange={(e) => setEditForm({ ...editForm, status: e.target.value as any })}
                    >
                      <option value="ACTIVE">Active</option>
                      <option value="INACTIVE">Inactive</option>
                      <option value="SUSPENDED">Suspended</option>
                    </select>
                  </div>

                  <div className="ump-form-group">
                    <label className="ump-form-label">System Role</label>
                    <div className="ump-role-radio-group">
                      <div
                        className={`ump-role-radio-card ${!editForm.isSuperAdmin ? 'selected' : ''}`}
                        onClick={() => {
                          if (editForm.id !== user?.id) {
                            setEditForm({ ...editForm, isSuperAdmin: false });
                          }
                        }}
                        style={{ cursor: editForm.id === user?.id ? 'not-allowed' : 'pointer' }}
                      >
                        <span className="ump-role-card-title">
                          <span>👤</span> Standard Member
                        </span>
                        <span className="ump-role-card-desc">
                          Permissions managed per module below.
                        </span>
                      </div>
                      <div
                        className={`ump-role-radio-card ${editForm.isSuperAdmin ? 'selected admin' : ''}`}
                        onClick={() => setEditForm({ ...editForm, isSuperAdmin: true })}
                      >
                        <span className="ump-role-card-title" style={{ color: editForm.isSuperAdmin ? '#6d28d9' : 'inherit' }}>
                          <span>★</span> Super Admin
                        </span>
                        <span className="ump-role-card-desc">
                          Unrestricted organization-wide access.
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="ump-form-group">
                    <label className="ump-form-label">Module Permissions</label>
                    <div className="ump-perms-row" style={{ marginTop: '2px' }}>
                      {[
                        { key: 'seo', label: 'SEO Intelligence' },
                        { key: 'ai', label: 'AI Assistant' },
                        { key: 'reports', label: 'Reports' },
                      ].map((mod) => {
                        const isChecked = editForm.isSuperAdmin || (editForm.moduleAccess as any)[mod.key] !== false;
                        return (
                          <button
                            key={mod.key}
                            type="button"
                            disabled={editForm.isSuperAdmin || editForm.id === user?.id}
                            className={`ump-perm-chip ${isChecked ? 'enabled' : 'disabled'} ${editForm.isSuperAdmin ? 'locked' : ''}`}
                            onClick={() => {
                              if (!editForm.isSuperAdmin && editForm.id !== user?.id) {
                                setEditForm({
                                  ...editForm,
                                  moduleAccess: {
                                    ...editForm.moduleAccess,
                                    [mod.key]: !isChecked
                                  }
                                });
                              }
                            }}
                          >
                            <span className="ump-perm-icon">
                              {isChecked ? (
                                <svg width="12" height="12" viewBox="0 0 20 20" fill="currentColor">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                                </svg>
                              ) : (
                                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                                  <line x1="18" y1="6" x2="6" y2="18" />
                                  <line x1="6" y1="6" x2="18" y2="18" />
                                </svg>
                              )}
                            </span>
                            {mod.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>

                <div className="ump-modal-footer">
                  <button
                    type="button"
                    className="ump-btn-cancel"
                    disabled={editLoading}
                    onClick={() => setIsEditOpen(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="ump-btn-submit"
                    disabled={editLoading}
                  >
                    {editLoading ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

      </div>
    </DashboardLayout>
  );
}
